import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { MapLedgerEventView, AmlLocation } from '@mark43/cad-api';
import { isValidLatLng } from '../../../../location/helpers';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { usePhoneCallsStore } from '../../../../phone-calls/use-phone-calls';
import { LocationAddressAndGpsSection } from '../location-address-and-gps-section';
import { AmlCallerInfo } from './aml-caller-info';
import { AmlCallOverview } from './aml-call-overview';
import { getEventIcon, getLocationConfidence } from '../helpers';
import { getEventPriorityAttributeId } from '../../../../esri/helpers/map-icon-theme';
import { UnitAssignedSection } from '../unit-panel/unit-assigned-section';
import { PanelWrapper } from '../panel-wrapper';
import { getLastAmlCallLocation } from '../../../../phone-calls/phone-calls-store';
import { useApplicationType } from '../../../../common/hooks/use-application-type';

export const AmlCallPanel = observer(function AmlCallPanel(): React.ReactElement | null {
    const esriMapStore = useEsriMapStore();
    const phoneCallsStore = usePhoneCallsStore();

    const { isFirstResponder } = useApplicationType();
    const size = isFirstResponder ? 'lg' : 'md';

    const removeAmlCallNotification = useCallback(() => {
        if (esriMapStore.selectedAmlPhoneNumberId) {
            phoneCallsStore.removePhoneCallNotifications(esriMapStore.selectedAmlPhoneNumberId);
        }
    }, [esriMapStore.selectedAmlPhoneNumberId, phoneCallsStore]);

    if (!esriMapStore.selectedAmlPhoneNumberId) return null;

    const selectedAmlCall = phoneCallsStore.getAmlCallByPhoneNumberId(
        esriMapStore.selectedAmlPhoneNumberId
    );

    const isAmlCallGpsNotification = phoneCallsStore.getPhoneCallNotification(
        esriMapStore.selectedAmlPhoneNumberId,
        'CALLER_GPS'
    );

    if (!selectedAmlCall) return null;

    const selectedEvent = esriMapStore.selectedMapLedgerEvent || ({} as MapLedgerEventView);
    const lastAmlLocation =
        getLastAmlCallLocation(selectedAmlCall.locations) || ({} as AmlLocation);
    const label = phoneCallsStore.getPhoneCallByPhoneNumberId(selectedAmlCall.phoneNumberId)
        ?.labelPrefix;
    const { latitude, longitude, uncertaintyRadius, confidencePercentage } = lastAmlLocation;
    const selectedMapViewportEvent = selectedEvent.entityId
        ? esriMapStore.getMapViewportEvent(selectedEvent.entityId)
        : undefined;

    return (
        <PanelWrapper testId="AML_CALL_PANEL">
            <AmlCallOverview
                label={label}
                createdDateUtc={selectedAmlCall.startTime}
                lastUpdated={lastAmlLocation.createdDateUtc}
                amlCallHasGpsNotification={isAmlCallGpsNotification}
                size={size}
            />
            {isValidLatLng(latitude, longitude) && (
                <LocationAddressAndGpsSection
                    isGpsLocation
                    placeName={selectedAmlCall.normalizedPhoneCallDisplay.callerAddress}
                    areGpsLocationCoordinatesVerified={
                        selectedAmlCall.normalizedPhoneCallDisplay.hasValidLatLng
                    }
                    gpsLocationLatitude={latitude}
                    gpsLocationLongitude={longitude}
                    confidenceValue={getLocationConfidence({
                        uncertaintyRadius,
                        confidencePercentage,
                    })}
                    amlCallHasGpsNotification={isAmlCallGpsNotification}
                    removeAmlCallNotification={removeAmlCallNotification}
                    size={size}
                />
            )}
            <AmlCallerInfo
                callerReportingParty={selectedAmlCall.normalizedPhoneCallDisplay.callerName}
                callerPhoneNumber={selectedAmlCall.phoneNumberDisplay}
                size={size}
            />
            {!!selectedEvent.entityId && (
                <UnitAssignedSection
                    eventId={selectedEvent.entityId}
                    eventIcon={getEventIcon(
                        true,
                        getEventPriorityAttributeId(selectedMapViewportEvent ?? selectedEvent),
                        selectedEvent.agencyTypeGlobalAttrId
                    )}
                    eventNumber={selectedEvent.agencyEventNumber!}
                    eventPriority={selectedEvent.priority!}
                    eventLocationNotes={selectedEvent.eventLocationNotes}
                    eventNarrative={selectedEvent.narrative}
                    cfsCode={selectedEvent.cfsCode!}
                    cfsDescription={selectedEvent.cfsDescription!}
                    placeName={selectedEvent.placeName}
                    locationSource={selectedEvent.locationSource}
                    isVerifiedLocation={selectedEvent.areCoordinatesVerified}
                    displayLineFirst={selectedEvent.displayLineFirst}
                    displayLineLast={selectedEvent.displayLineLast}
                    displayLineOptionalMiddle={selectedEvent.displayLineOptionalMiddle}
                    eventDisplayLineSubPremise={selectedEvent.displayLineSubPremise}
                    latitudeCoordinate={selectedEvent.geoLocation?.latitude}
                    longitudeCoordinate={selectedEvent.geoLocation?.longitude}
                    size={size}
                />
            )}
        </PanelWrapper>
    );
});
